'use client';

import Script from 'next/script';
import { usePathname } from 'next/navigation';
import { useLayoutEffect } from 'react';

const clarityProjectId =
  process.env.NEXT_PUBLIC_MICROSOFT_CLARITY_PROJECT_ID?.trim();

const excludedPathPrefixes = [
  '/auth',
  '/checkout',
  '/dashboard',
  '/kyc',
  '/onboarding',
  '/order',
  '/profile',
  '/sign-in',
  '/sign-up',
];

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

function isValidClarityProjectId(projectId: string): boolean {
  return /^[a-z0-9]+$/i.test(projectId);
}

function isExcludedPath(pathname: string): boolean {
  return excludedPathPrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function getClaritySnippet(projectId: string): string {
  return `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", ${JSON.stringify(projectId)});
  `;
}

export default function MicrosoftClarity() {
  const pathname = usePathname();
  const isBlockedPath = isExcludedPath(pathname);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.clarity?.(isBlockedPath ? 'pause' : 'resume');
  }, [isBlockedPath]);

  if (
    !clarityProjectId ||
    !isValidClarityProjectId(clarityProjectId) ||
    isBlockedPath
  ) {
    return null;
  }

  return (
    <Script
      id="microsoft-clarity"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: getClaritySnippet(clarityProjectId),
      }}
    />
  );
}
